import React from "react";
import * as Icons from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { FeatureCard } from "@/components/blocks/FeatureCard";
import { FAQ } from "@/components/blocks/FAQ";
import { CtaBand } from "@/components/blocks/CtaBand";
import { QuoteSection } from "@/components/blocks/QuoteSection";
import { Reveal } from "@/components/common/Reveal";
import { ApiSnippet } from "@/components/visuals/ApiSnippet";
import { contactLink } from "@/data/site";

const TYPES = [
  { icon: "Megaphone", title: "Promotional SMS", desc: "Marketing and promotional communication to reach and re-engage customers." },
  { icon: "ReceiptText", title: "Transactional SMS", desc: "Important service and transaction updates delivered reliably." },
  { icon: "KeyRound", title: "OTP SMS", desc: "Fast, dependable one-time-passwords for authentication and verification." },
  { icon: "BellRing", title: "Alerts & Notifications", desc: "Operational and customer notifications when timing matters." },
];

const FEATURES = [
  { icon: "Zap", title: "Fast Delivery", desc: "Reach large audiences quickly and reliably." },
  { icon: "BadgeCheck", title: "Sender ID Support", desc: "Send with a recognisable sender identity." },
  { icon: "KeyRound", title: "OTP", desc: "Time-bound verification codes at scale." },
  { icon: "ReceiptText", title: "Transactional", desc: "Service and account related messaging." },
  { icon: "Megaphone", title: "Promotional", desc: "Campaigns for offers and announcements." },
  { icon: "CalendarClock", title: "Scheduling", desc: "Plan and schedule messages in advance." },
  { icon: "FileBarChart", title: "Delivery Reports", desc: "Track delivery status and outcomes." },
  { icon: "Code2", title: "API Integration", desc: "Send SMS directly from your applications." },
];

const FAQS = [
  { q: "What types of SMS can I send?", a: "You can send promotional, transactional, OTP and alert/notification messages depending on your use case and applicable regulations." },
  { q: "Do you support Sender ID?", a: "Yes, sender ID support helps recipients recognise your business. Availability depends on regulatory registration (such as DLT in India)." },
  { q: "Is there an SMS API?", a: "Yes. E Telecom offers API integration so you can send SMS directly from your own applications and workflows." },
  { q: "Do I get delivery reports?", a: "Delivery reporting is available so you can track the status and outcome of your messages." },
];

export default function BulkSMS() {
  useSEO({
    title: "Bulk SMS Service Provider in India | SMS API | E Telecom",
    description: "Send promotional, transactional and OTP SMS at scale with E Telecom. Fast delivery, sender ID, scheduling, delivery reports and SMS API integration.",
    path: "/bulk-sms",
  });

  return (
    <>
      <PageHero
        eyebrow="Bulk SMS Services"
        icon="MessageCircle"
        title="Reach Customers Instantly with Bulk SMS"
        description="Deliver alerts, notifications, promotions and transactional messages quickly and reliably — with delivery reporting and easy API integration."
        primary={{ to: "/bulk-sms#quote", label: "Request Pricing" }}
        secondary={{ to: contactLink("Bulk SMS"), label: "Talk to Sales" }}
      />

      {/* SMS types */}
      <section className="et-section bg-white">
        <div className="et-container">
          <SectionHeading eyebrow="SMS Types" title="The right message for every need" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TYPES.map((t, i) => <FeatureCard key={t.title} icon={t.icon} title={t.title} description={t.desc} delay={(i % 4) * 0.05} />)}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="et-section">
        <div className="et-container">
          <SectionHeading eyebrow="Features" title="Built for reliable business messaging" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => <FeatureCard key={f.title} icon={f.icon} title={f.title} description={f.desc} delay={(i % 4) * 0.05} />)}
          </div>
        </div>
      </section>

      {/* API section */}
      <section className="et-section bg-white">
        <div className="et-container grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <span className="et-eyebrow">Developer Friendly</span>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mt-4">Integrate SMS Into Your Applications</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Trigger OTPs, alerts and notifications directly from your product, website or backend using a simple SMS API. Send transactional and promotional messages programmatically with delivery tracking.</p>
            <ul className="mt-6 space-y-3">
              {["Simple REST API with clear responses", "Send OTP, transactional and promotional SMS", "Delivery status and reporting", "India DLT-aware sender registration support"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><Icons.CheckCircle2 className="h-5 w-5 text-[var(--et-primary)] mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-400">API endpoint and payload shown are illustrative examples for demonstration.</p>
          </Reveal>
          <Reveal delay={0.1}><ApiSnippet /></Reveal>
        </div>
      </section>

      <FAQ items={FAQS} title="Bulk SMS FAQs" />
      <QuoteSection service="Bulk SMS" title="Request Bulk SMS Pricing" subtitle="Share your SMS requirement — promotional, transactional or OTP — and our team will get back to you with pricing." testIdPrefix="bulksms-quote" />
      <CtaBand title="Start sending SMS that gets delivered" subtitle="Talk to our team about promotional, transactional and OTP SMS for your business." primary={{ to: "/bulk-sms#quote", label: "Request Pricing" }} secondary={{ to: contactLink("Bulk SMS"), label: "Talk to Sales" }} />
    </>
  );
}
